'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import { VerticalTabsProps } from './types';

import IconArrow from '@/assets/icons/iconBaselinePlayArrow.svg';
import { useCurrentLocale, useGetClientHost } from '@/shared/hooks';
import { classNames } from '@/shared/lib/classNames/classNames';
import { getDomainKey } from '@/shared/utils/getDomainInfo';

export const VerticalTabs: React.FC<VerticalTabsProps> = ({
  tabs,
  className,
  defaultActiveTab = 0,
}) => {
  const [activeTabIndex, setActiveTabIndex] =
    useState<number>(defaultActiveTab);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const clientHost = useGetClientHost();
  const currentLocale = useCurrentLocale();
  const currentDomain = getDomainKey(clientHost);

  /**
   * Фильтрует вкладки по домену и локали.
   * Если указаны оба поля, вкладка отображается
   * только при совпадении обоих условий.
   */
  const filteredTabs = useMemo(
    () =>
      tabs.filter((tab) => {
        if (tab.domains && !tab.domains.includes(currentDomain)) {
          return false;
        }

        if (tab.locales && !tab.locales.includes(currentLocale)) {
          return false;
        }

        return true;
      }),
    [tabs, currentDomain, currentLocale],
  );

  useEffect(() => {
    if (!isDropdownOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleTabClick = (index: number) => {
    setActiveTabIndex(index);
    setIsDropdownOpen(false);
  };

  if (!filteredTabs.length) {
    return null;
  }

  const activeTab = filteredTabs[activeTabIndex];

  return (
    <div className={classNames({ cls: 'w-full', additional: [className] })}>
      <div className="relative mb-6 block lg:hidden">
        <div
          className="bg-surface-base-secondary flex min-h-[60px] cursor-pointer items-center justify-between rounded-[12px] px-5"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <div className="flex items-center">
            <div className="mr-2">{activeTab.icon}</div>

            <p className="text-text-base-primary text-[16px] leading-[24px] font-normal">
              {activeTab.label}
            </p>
          </div>

          <IconArrow
            className={classNames({
              cls: 'h-[18px] min-h-[18px] w-[18px] min-w-[18px] transition-transform',
              mods: {
                'rotate-180': isDropdownOpen,
              },
            })}
          />
        </div>

        {isDropdownOpen && (
          <div
            className="bg-surface-base-secondary absolute top-full left-0 z-[11] mt-1 w-full rounded-[10px] p-3 shadow-md"
            ref={dropdownRef}
          >
            {filteredTabs.map((tab, index) => (
              <div
                key={tab.label}
                onClick={() => handleTabClick(index)}
                className={classNames({
                  cls: 'flex min-h-[48px] w-full cursor-pointer items-center rounded-[12px] px-2 py-2',
                  mods: {
                    'bg-surface-base-tertiary': activeTabIndex === index,
                  },
                })}
              >
                <div className="mr-2 flex h-full [&>svg]:h-[24px] [&>svg]:w-[24px]">
                  {tab.icon}
                </div>

                <p className="text-text-base-primary text-[14px] leading-[20px] font-normal">
                  {tab.label}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-[349px_1fr] max-lg:grid-cols-1">
        <div className="w-full max-lg:hidden">
          {filteredTabs.map((tab, index) => (
            <div
              id={tab.labelId}
              key={tab.label}
              onClick={() => handleTabClick(index)}
              className={classNames({
                cls: 'flex min-h-[48px] w-full cursor-pointer rounded-[12px] px-3 py-3',
                mods: {
                  'bg-surface-base-tertiary': activeTabIndex === index,
                },
              })}
            >
              <div className="mr-2 flex [&>svg]:h-[24px] [&>svg]:w-[24px]">
                {tab.icon}
              </div>
              <p className="text-text-base-primary h-full text-[16px] leading-[24px] font-normal">
                {tab.label}
              </p>
            </div>
          ))}
        </div>

        <div className="w-full">{activeTab.content}</div>
      </div>
    </div>
  );
};
